import ActivationModel from "../ActivationModel";
import { getMotionOverlayEnabled } from "../debugSettings";
import { createZoneMask } from "../zoneMask";

// Standalone three-frame differencing motion detector, ported from the
// motion-test benchmark's "Three-frame differencing" algorithm. A pixel only
// counts as changed when the current frame differs from BOTH of the two
// previous frames (|cur − prev| AND |cur − prev2|), which suppresses the
// ghosting and lighting-step artifacts plain two-frame differencing leaves
// behind, and adapts instantly to gradual lighting drift.

const DETECTION_INTERVAL_MS = 250;
const MAX_INPUT_HEIGHT = 360;
// Dark frames are mostly sensor noise, which flips pixels past the diff
// threshold at random — suppress them, same rule as the diff detector.
const MIN_BRIGHT_PIXEL_RATIO = 0.1;

// Consecutive active time required before motion arms, and quiet time after
// the last active frame before it releases.
const ARM_DURATION_MS = 750;
const RELEASE_DELAY_MS = 1000;

// diffThreshold: gray-level change for a pixel to count as different.
// minAreaPercent: share of (zone) pixels that must change for a frame to be
// active. Values match the motion-test benchmark presets.
export const ThreeFrameSens = {
  HIGH: { diffThreshold: 12, minAreaPercent: 0.1 },
  MEDIUM: { diffThreshold: 20, minAreaPercent: 0.3 },
  LOW: { diffThreshold: 35, minAreaPercent: 1 }
} as const;

type ThreeFrameMotionDetectorConfig = {
  sensitivity?: MotionDetectionSensitivity;
  onChange?: (isMotion: boolean) => void;
};

type FrameResult = { changedRatio: number; suppressed: boolean };

export default class ThreeFrameMotionDetector {
  state: "idle" | "running" = "idle";
  isMotion = false;

  private video: HTMLVideoElement | null = null;
  private sensitivity: MotionDetectionSensitivity;
  private onChange?: (isMotion: boolean) => void;

  private cv: any = null;
  private canvas: HTMLCanvasElement | null = null;
  private context: CanvasRenderingContext2D | null = null;
  private grayFrame: any = null;
  private previous: any = null;
  private beforePrevious: any = null;
  private diffA: any = null;
  private diffB: any = null;
  private motionMask: any = null;
  private zoneMask: any = null;
  private zoneElements = 0;
  private gridMask: MotionDetectionZone["gridMask"] | null = null;
  private timeoutId: NodeJS.Timeout | null = null;
  private debugBox: ReturnType<ThreeFrameMotionDetector["createDebugBox"]> | null = null;

  private model = new ActivationModel({
    armFrames: Math.max(1, Math.round(ARM_DURATION_MS / DETECTION_INTERVAL_MS)),
    releaseDelayMs: RELEASE_DELAY_MS
  });

  constructor({ sensitivity = "MEDIUM", onChange }: ThreeFrameMotionDetectorConfig) {
    this.sensitivity = sensitivity;
    this.onChange = onChange;
  }

  setSensitivity = (sensitivity: MotionDetectionSensitivity) => {
    log.motion("Three-frame sensitivity changed:", sensitivity);
    this.sensitivity = sensitivity;
  };

  /** Pass null to detect over the whole frame. */
  setDetectionZone = (detectionZone: MotionDetectionZone | null) => {
    this.gridMask = detectionZone?.gridMask ?? null;
    this.zoneMask?.delete();
    this.zoneMask = null;
    this.zoneElements = 0;
  };

  start = async (video: HTMLVideoElement) => {
    if (this.state === "running") return;
    this.video = video;

    if (video.readyState < HTMLMediaElement.HAVE_CURRENT_DATA) {
      log.motion("Three-frame waiting for video 'loadeddata'...");
      video.addEventListener("loadeddata", () => this.start(video), { once: true });
      return;
    }

    this.state = "running";
    const cv = await window.cv;
    if (!cv) {
      log.warn("Couldn't start three-frame motion detector, OpenCV is not loaded");
      this.state = "idle";
      return;
    }
    if (this.state !== "running") return; // stopped while awaiting OpenCV

    log.info("About to start three-frame video processing for motion detection");
    this.cv = cv;
    this.canvas = document.createElement("canvas");
    this.context = this.canvas.getContext("2d", { willReadFrequently: true });
    this.grayFrame = new cv.Mat();
    this.diffA = new cv.Mat();
    this.diffB = new cv.Mat();
    this.motionMask = new cv.Mat();
    if (getMotionOverlayEnabled()) this.debugBox = this.createDebugBox();
    this.processVideo();
  };

  stop = () => {
    log.info("Stopping three-frame video processing for motion");
    if (this.timeoutId) clearTimeout(this.timeoutId);
    this.timeoutId = null;
    this.state = "idle";
    this.model.reset();
    this.isMotion = false;

    this.debugBox?.remove();
    this.debugBox = null;
    this.grayFrame?.delete();
    this.previous?.delete();
    this.beforePrevious?.delete();
    this.diffA?.delete();
    this.diffB?.delete();
    this.motionMask?.delete();
    this.zoneMask?.delete();
    this.grayFrame = null;
    this.previous = null;
    this.beforePrevious = null;
    this.diffA = null;
    this.diffB = null;
    this.motionMask = null;
    this.zoneMask = null;
    this.zoneElements = 0;
    this.canvas = null;
    this.context = null;
  };

  forceRest = () => {
    this.model.reset();
    this.emitIfChanged(false);
  };

  private processVideo = () => {
    if (this.state !== "running") return;

    try {
      const frameStartMs = performance.now();
      const { changedRatio, suppressed } = this.detectFrame();
      const minAreaRatio = ThreeFrameSens[this.sensitivity].minAreaPercent / 100;
      const isMotion = this.model.update(changedRatio, minAreaRatio, performance.now(), suppressed);
      this.emitIfChanged(isMotion);
      this.debugBox?.update({
        changedRatio,
        minAreaRatio,
        suppressed,
        isMotion,
        processingMs: performance.now() - frameStartMs
      });
    } catch (err) {
      log.err("Failed three-frame 'processVideo'", err);
      this.model.reset();
      this.emitIfChanged(false);
    }

    this.timeoutId = setTimeout(this.processVideo, DETECTION_INTERVAL_MS);
  };

  private detectFrame = (): FrameResult => {
    const cv = this.cv;
    const frame = cv.matFromImageData(this.captureFrame());

    try {
      cv.cvtColor(frame, this.grayFrame, cv.COLOR_RGBA2GRAY);
      cv.GaussianBlur(this.grayFrame, this.grayFrame, new cv.Size(5, 5), 0);

      const sizeChanged =
        this.previous && (this.previous.rows !== this.grayFrame.rows || this.previous.cols !== this.grayFrame.cols);
      if (sizeChanged) {
        this.previous?.delete();
        this.beforePrevious?.delete();
        this.previous = null;
        this.beforePrevious = null;
      }

      // Warm-up: the differencing needs two stored frames before it can run.
      if (!this.previous) {
        this.previous = this.grayFrame.clone();
        return { changedRatio: 0, suppressed: true };
      }
      if (!this.beforePrevious) {
        this.beforePrevious = this.previous;
        this.previous = this.grayFrame.clone();
        return { changedRatio: 0, suppressed: true };
      }

      const brightPixelRatio = cv.countNonZero(this.grayFrame) / (this.grayFrame.rows * this.grayFrame.cols);
      const tooDark = brightPixelRatio <= MIN_BRIGHT_PIXEL_RATIO;
      if (tooDark) log.motion("Three-frame brightness too low");

      const { diffThreshold } = ThreeFrameSens[this.sensitivity];
      cv.absdiff(this.grayFrame, this.previous, this.diffA);
      cv.absdiff(this.grayFrame, this.beforePrevious, this.diffB);
      cv.threshold(this.diffA, this.diffA, diffThreshold, 255, cv.THRESH_BINARY);
      cv.threshold(this.diffB, this.diffB, diffThreshold, 255, cv.THRESH_BINARY);
      cv.bitwise_and(this.diffA, this.diffB, this.motionMask);
      cv.medianBlur(this.motionMask, this.motionMask, 3);

      // Small zones would otherwise dilute the ratio, so count changed pixels
      // inside the zone against the zone's own area, like the diff detector.
      const zoneMask = this.ensureZoneMask();
      if (zoneMask) cv.bitwise_and(this.motionMask, zoneMask, this.motionMask);
      const elements = zoneMask ? this.zoneElements : this.motionMask.rows * this.motionMask.cols;
      const changedRatio = elements > 0 ? cv.countNonZero(this.motionMask) / elements : 0;

      // Rotate the frame history, reusing the oldest mat's buffer.
      const recycled = this.beforePrevious;
      this.beforePrevious = this.previous;
      this.previous = recycled;
      this.grayFrame.copyTo(this.previous);

      return { changedRatio, suppressed: tooDark };
    } finally {
      frame.delete();
    }
  };

  private captureFrame = () => {
    const video = this.video as HTMLVideoElement;
    const canvas = this.canvas as HTMLCanvasElement;
    const context = this.context as CanvasRenderingContext2D;

    const scale = Math.min(1, MAX_INPUT_HEIGHT / video.videoHeight);
    const width = Math.max(1, Math.round(video.videoWidth * scale));
    const height = Math.max(1, Math.round(video.videoHeight * scale));
    if (canvas.width !== width) canvas.width = width;
    if (canvas.height !== height) canvas.height = height;

    context.drawImage(video, 0, 0, width, height);
    return context.getImageData(0, 0, width, height);
  };

  // The mask lives in the detection frame's coordinate space and rebuilds
  // when the frame size changes.
  private ensureZoneMask = () => {
    if (!this.gridMask) return null;

    const { rows: frameRows, cols: frameCols } = this.motionMask;
    if (this.zoneMask && this.zoneMask.rows === frameRows && this.zoneMask.cols === frameCols) {
      return this.zoneMask;
    }
    this.zoneMask?.delete();
    this.zoneMask = null;
    this.zoneElements = 0;

    try {
      this.zoneMask = createZoneMask(this.cv, this.gridMask, frameRows, frameCols);
      this.zoneElements = this.cv.countNonZero(this.zoneMask);
      log.motion("Created three-frame detection zone mask", frameCols, "x", frameRows);
    } catch (e) {
      log.err("Failed to create three-frame detection zone mask", e);
    }
    return this.zoneMask;
  };

  private emitIfChanged = (isMotion: boolean) => {
    if (isMotion === this.isMotion) return;
    this.isMotion = isMotion;
    this.onChange?.(isMotion);
  };

  private createDebugBox = () => {
    const cameraContainer = document.querySelector("#camera-video-container");
    const canvas = document.createElement("canvas");
    canvas.id = "three-frame-debug-container";
    canvas.style.position = "absolute";
    canvas.style.top = "0px";
    canvas.style.left = "0px";
    canvas.style.width = "100%";
    canvas.style.height = "100%";
    canvas.style.opacity = "0.2";
    canvas.style.transform = "scaleX(-1)";
    cameraContainer?.appendChild(canvas);

    const stats = document.createElement("div");
    stats.style.position = "absolute";
    stats.style.top = "8px";
    stats.style.left = "8px";
    stats.style.padding = "4px 8px";
    stats.style.background = "rgba(0, 0, 0, 0.6)";
    stats.style.color = "white";
    stats.style.font = "12px monospace";
    stats.style.pointerEvents = "none";
    cameraContainer?.appendChild(stats);

    type DebugFrame = {
      changedRatio: number;
      minAreaRatio: number;
      suppressed: boolean;
      isMotion: boolean;
      processingMs: number;
    };

    return {
      update: ({ changedRatio, minAreaRatio, suppressed, isMotion, processingMs }: DebugFrame) => {
        if (this.motionMask?.rows) this.cv.imshow("three-frame-debug-container", this.motionMask);
        const status = suppressed ? "suppressed" : isMotion ? "MOTION" : "rest";
        stats.textContent =
          `changed ${(changedRatio * 100).toFixed(2)}% / min ${(minAreaRatio * 100).toFixed(2)}% · ` +
          `frame ${processingMs.toFixed(1)} ms · ${status}`;
      },
      remove: () => {
        canvas.remove();
        stats.remove();
      }
    };
  };
}
